import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { hasPermission, isOwner, ROLES } from '../../utils/rolePermissions';
import {
  fetchMyPermissions,
  selectMyPermissions,
  selectHasPermission,
  selectHasAnyPermission,
  selectHasAllPermissions,
  selectIsOwner
} from './permissionsSlice';

/**
 * RoleGuard Component
 * Controls access to UI elements based on user roles and permissions
 * Supports both legacy role-based and new enterprise permission system
 *
 * @param {ReactNode} children - Content to render if permission is granted
 * @param {string} permission - Permission string in format "feature:action" or new format "resource:action:scope"
 * @param {Array<string>} anyPermission - Array of permissions (user needs any one)
 * @param {Array<string>} allPermissions - Array of permissions (user needs all)
 * @param {ReactNode} fallback - Optional content to render if permission is denied
 *
 * Usage:
 * <RoleGuard permission="products:view:all">
 *   <ProductsList />
 * </RoleGuard>
 *
 * <RoleGuard anyPermission={['orders:view:all', 'orders:view:own']}>
 *   <OrdersList />
 * </RoleGuard>
 */
export default function RoleGuard({
  children,
  permission,
  anyPermission,
  allPermissions,
  fallback = null
}) {
  const { user } = useSecureAuth();
  const myPermissions = useSelector(selectMyPermissions);

  // Get user role, default to OWNER if not set
  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;

  // Owner always has access
  if (userRole === ROLES.OWNER || myPermissions?.isOwner || user?.ownsStore) {
    return children;
  }

  // Combine permissions from Redux (fetchMyPermissions) AND user context (from profile endpoint)
  // This ensures permissions work even before fetchMyPermissions completes
  const reduxPermissions = myPermissions?.permissionCodes || [];
  const userContextPermissions = user?.permissions || [];
  const permissionCodes = reduxPermissions.length > 0 ? reduxPermissions : userContextPermissions;

  if (permissionCodes.length > 0) {
    // New enterprise permission system
    if (permission) {
      if (permissionCodes.includes(permission)) {
        return children;
      }
      // Also check legacy format for backward compatibility
      const legacyCode = permission.split(':').slice(0, 2).join(':');
      if (permissionCodes.some(p => p.startsWith(legacyCode))) {
        return children;
      }
      return fallback;
    }

    if (anyPermission && anyPermission.length > 0) {
      const hasAny = anyPermission.some(p => permissionCodes.includes(p));
      return hasAny ? children : fallback;
    }

    if (allPermissions && allPermissions.length > 0) {
      const hasAll = allPermissions.every(p => permissionCodes.includes(p));
      return hasAll ? children : fallback;
    }

    return fallback;
  }

  // Fallback to legacy role-based permission system
  if (permission) {
    const [feature, rawAction] = permission?.split(':') || [];

    if (!feature || !rawAction) {
      console.warn(`Invalid permission format: ${permission}. Expected "feature:action"`);
      return fallback;
    }

    const actionMap = {
      'read': 'view',
      'write': 'edit',
      'view': 'view',
      'edit': 'edit',
      'create': 'create',
      'delete': 'delete'
    };

    const action = actionMap[rawAction] || rawAction;
    const hasAccess = hasPermission(userRole, feature, action);
    return hasAccess ? children : fallback;
  }

  return fallback;
}

/**
 * Hook to check permissions in components
 * Supports both legacy and enterprise permission systems
 * @param {string} permissionCode - Permission code (e.g., 'products:view:all')
 * @returns {boolean} - Whether user has permission
 */
export function usePermission(permissionCode) {
  const { user } = useSecureAuth();
  const myPermissions = useSelector(selectMyPermissions);

  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;

  // Owner always has access
  if (userRole === ROLES.OWNER || myPermissions?.isOwner || user?.ownsStore) {
    return true;
  }

  // Check enterprise permissions (combine Redux and user context)
  const reduxPermissions = myPermissions?.permissionCodes || [];
  const userContextPermissions = user?.permissions || [];
  const permissionCodes = reduxPermissions.length > 0 ? reduxPermissions : userContextPermissions;

  // Check for wildcard
  if (permissionCodes.includes('*')) {
    return true;
  }

  // Direct match
  if (permissionCodes.includes(permissionCode)) {
    return true;
  }

  // Check for resource wildcard (e.g., 'products:*' matches 'products:view:all')
  const [resource, action] = permissionCode?.split(':') || [];
  if (resource && permissionCodes.includes(`${resource}:*`)) {
    return true;
  }

  // Fallback to legacy
  if (resource && action) {
    const actionMap = { 'read': 'view', 'write': 'edit' };
    const normalizedAction = actionMap[action] || action;
    return hasPermission(userRole, resource, normalizedAction);
  }

  return false;
}

/**
 * Hook to check if user has any of the specified permissions
 * @param {Array<string>} permissionCodes - Array of permission codes
 * @returns {boolean} - Whether user has any permission
 */
export function useAnyPermission(permissionCodesToCheck) {
  const { user } = useSecureAuth();
  const myPermissions = useSelector(selectMyPermissions);

  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;

  if (userRole === ROLES.OWNER || myPermissions?.isOwner || user?.ownsStore) {
    return true;
  }

  // Combine permissions from Redux and user context
  const reduxPermissions = myPermissions?.permissionCodes || [];
  const userContextPermissions = user?.permissions || [];
  const userPermissions = reduxPermissions.length > 0 ? reduxPermissions : userContextPermissions;

  // Check for wildcard
  if (userPermissions.includes('*')) {
    return true;
  }

  return permissionCodesToCheck.some(p => {
    if (userPermissions.includes(p)) return true;
    // Check resource wildcard
    const [resource] = p.split(':');
    return resource && userPermissions.includes(`${resource}:*`);
  });
}

/**
 * Hook to check if user has all of the specified permissions
 * @param {Array<string>} permissionCodes - Array of permission codes
 * @returns {boolean} - Whether user has all permissions
 */
export function useAllPermissions(permissionCodesToCheck) {
  const { user } = useSecureAuth();
  const myPermissions = useSelector(selectMyPermissions);

  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;

  if (userRole === ROLES.OWNER || myPermissions?.isOwner || user?.ownsStore) {
    return true;
  }

  // Combine permissions from Redux and user context
  const reduxPermissions = myPermissions?.permissionCodes || [];
  const userContextPermissions = user?.permissions || [];
  const userPermissions = reduxPermissions.length > 0 ? reduxPermissions : userContextPermissions;

  // Check for wildcard
  if (userPermissions.includes('*')) {
    return true;
  }

  return permissionCodesToCheck.every(p => {
    if (userPermissions.includes(p)) return true;
    // Check resource wildcard
    const [resource] = p.split(':');
    return resource && userPermissions.includes(`${resource}:*`);
  });
}

/**
 * Hook to get user's role
 * @returns {string} - User's role name
 */
export function useUserRole() {
  const { user } = useSecureAuth();
  const myPermissions = useSelector(selectMyPermissions);

  // Return enterprise role name if available
  if (myPermissions?.roleName) {
    return myPermissions.roleName;
  }

  return user?.merchantRole?.toUpperCase() || ROLES.OWNER;
}

/**
 * Hook to check if user is store owner
 * @returns {boolean} - Whether user is owner
 */
export function useIsOwner() {
  const { user } = useSecureAuth();
  const myPermissions = useSelector(selectMyPermissions);

  // Check multiple sources for owner status
  if (myPermissions?.isOwner) {
    return true;
  }

  if (user?.ownsStore) {
    return true;
  }

  const role = user?.merchantRole?.toUpperCase() || ROLES.OWNER;
  return isOwner(role);
}

/**
 * Hook to fetch and use the current user's effective permissions
 * @returns {Object} - { permissions, isLoading, isOwner, role }
 */
export function useEffectivePermissions() {
  const dispatch = useDispatch();
  const myPermissions = useSelector(selectMyPermissions);
  const { user } = useSecureAuth();

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchMyPermissions());
    }
  }, [dispatch, user?.id]);

  // Combine permissions from Redux and user context
  const reduxPermissions = myPermissions?.permissionCodes || [];
  const userContextPermissions = user?.permissions || [];
  const permissions = reduxPermissions.length > 0 ? reduxPermissions : userContextPermissions;

  return {
    permissions,
    isOwner: myPermissions?.isOwner || user?.merchantRole === 'OWNER' || user?.ownsStore,
    role: myPermissions?.roleName || user?.roleName || user?.merchantRole,
    fullPermissions: myPermissions?.permissions || []
  };
}
